import React, { useEffect, useMemo, useState } from "react";
import Switch from "@material-ui/core/Switch";
import Button from "../../shared/Button";
import swal from "sweetalert";
import { useARRCode } from "../../../context/ARRLevelContext";
import CustomModal from "../../shared/CustomModal";
import {
  useAssessmentScale,
  useAssociateAssessmentScale,
  useSelectAssessmentScaleLevel,
} from "../../../hooks/useAssessmentScale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "reactstrap";
import Select from "../../shared/Select";
import { URL } from "../../../../../utils/Constants";
import { Tooltip } from "@mui/material";

const showScoreAsPercentageOption = [
  {
    label: "Show both score and percentage",
    value: "both",
  },
  {
    label: "Show Percentage",
    value: "pctg",
  },
  {
    label: "Don't show percentage",
    value: "Nopctg",
  },
];

const showAggregationAsPercentageOption = [
  {
    label: "Show both aggregation and percentage",
    value: "aggregateAndPer",
  },
  {
    label: "Show only percentage",
    value: "per",
  },
  {
    label: "Don't show percentage",
    value: "noPer",
  },
];

const subjectTypeOptions = [
  {
    label: "Theory",
    value: "theory",
  },
  {
    label: "Practical",
    value: "practical",
  },
  {
    label: "Both",
    value: "both",
  },
];

const MarksAssessmentForm = ({
  data,
  type,
  scaleType,
  updating,
  handleUpdateAssessment,
  closeModal,
}) => {
  const { loading, createAssessmentScale } = useAssessmentScale();
  const { arrLevel } = useARRCode();
  const { associateAssessmentLevel, getAssociateAssessmentLevel } =
    useAssociateAssessmentScale();
  const { assessmentScaleLevel, selectAssessmentScaleLevel } =
    useSelectAssessmentScaleLevel();
  const [associateLevelModal, setAssociateLevelModal] = useState(false);
  const [marksAssessmentScale, setMarksAssessmentScale] = useState({
    scaleName: "",
    scoreName: "",
    totalMarks: "",
    subjectType: "theory",
    theoryPassMarks: "",
    theoryFullMarks: "",
    practicalPassMarks: "",
    practicalFullMarks: "",
    showScoreAsPercentage: "",
    calculateAggregate: false,
    showAggregationAspercentage: "",
  });
  const [associateScalePercent, setAssociateScalePercent] = useState([]);
  const [associateSelectInput, setAssociateSeletInput] = useState(null);

  const isView = type === "view";

  const associateAssessmentLevelOptions = useMemo(() => {
    if (!associateAssessmentLevel) return;
    return associateAssessmentLevel?.map((item) => ({
      label: item.scaleName,
      value: item.id,
    }));
  }, [associateAssessmentLevel]);

  const handleMarksAssessmentChange = (e) => {
    const { name, value } = e.target;

    if (name === "theoryFullMarks") {
      const totalMarks =
        Number(marksAssessmentScale.practicalFullMarks) + Number(value);

      setMarksAssessmentScale((prev) => ({
        ...prev,
        totalMarks: totalMarks,
        [name]: value,
      }));

      return;
    }

    if (name === "practicalFullMarks") {
      const totalMarks =
        Number(marksAssessmentScale.theoryFullMarks) + Number(value);

      setMarksAssessmentScale((prev) => ({
        ...prev,
        totalMarks: totalMarks,
        [name]: value,
      }));

      return;
    }

    setMarksAssessmentScale((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubjectTypeChange = (option) => {
    const subjectType = option?.value;
    const theoryPassMarks =
      subjectType !== "practical" ? marksAssessmentScale.theoryPassMarks : "";
    const theoryFullMarks =
      subjectType !== "practical" ? marksAssessmentScale.theoryFullMarks : "";
    const practicalPassMarks =
      subjectType !== "theory" ? marksAssessmentScale.practicalPassMarks : "";
    const practicalFullMarks =
      subjectType !== "theory" ? marksAssessmentScale.practicalFullMarks : "";

    const totalMarks =
      Number(theoryFullMarks || 0) + Number(practicalFullMarks || 0);

    setMarksAssessmentScale((prev) => ({
      ...prev,
      theoryPassMarks,
      theoryFullMarks,
      practicalPassMarks,
      practicalFullMarks,
      subjectType,
      totalMarks,
    }));
  };

  const validateNegativeNumber = (e) => {
    if (e.key === "-" || e.key === "Subtract" || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    if (!marksAssessmentScale?.scaleName.trim()) {
      swal("Oops", "Scale Name is required");
      return;
    }

    if (!marksAssessmentScale?.scoreName.trim()) {
      swal("Oops", "Score Name is required");
      return;
    }

    if (!marksAssessmentScale.showScoreAsPercentage) {
      swal("Oops", "Show Score As Percentage is required");
      return;
    }

    if (
      marksAssessmentScale.subjectType !== "practical" &&
      (marksAssessmentScale.theoryPassMarks === "" ||
        marksAssessmentScale.theoryFullMarks === "")
    ) {
      swal("Oops", "Theory marks is required");
      return;
    }

    if (
      marksAssessmentScale.subjectType !== "practical" &&
      Number(marksAssessmentScale.theoryFullMarks ?? 0) <
        Number(marksAssessmentScale.theoryPassMarks ?? 0)
    ) {
      swal("Oops", "Theory full marks cannot be less than pass marks");
      return;
    }

    if (
      marksAssessmentScale.subjectType !== "theory" &&
      (marksAssessmentScale.practicalFullMarks === "" ||
        marksAssessmentScale.practicalPassMarks === "")
    ) {
      swal("Oops", "Practical marks is required");
      return;
    }

    if (
      marksAssessmentScale.subjectType !== "theory" &&
      Number(marksAssessmentScale.practicalFullMarks ?? 0) <
        Number(marksAssessmentScale.practicalPassMarks ?? 0)
    ) {
      swal("Oops", "Practical full marks cannot be less than pass marks");
      return;
    }

    if (!marksAssessmentScale.showAggregationAspercentage) {
      swal("Oops", "Please select Show Aggregation As Percentage");
      return;
    }

    const assessmentScaleMarkLevels =
      associateScalePercent?.length > 0
        ? associateScalePercent?.map((item) => ({
            rangeMin: item.rangeMin,
            rangeMax: item.rangeMax,
            levelId: item.id,
          }))
        : [];

    const formData = {
      assessmentScaleTypeCode: scaleType,
      scaleName: marksAssessmentScale.scaleName,
      scoreName: marksAssessmentScale.scoreName,
      assessmentScaleMark: {
        rangeMax: marksAssessmentScale.totalMarks,
        showPercentage:
          marksAssessmentScale.showScoreAsPercentage === "pctg"
            ? true
            : marksAssessmentScale.showScoreAsPercentage === "both"
              ? true
              : false,
        showScore:
          marksAssessmentScale.showScoreAsPercentage === "Nopctg"
            ? true
            : marksAssessmentScale.showScoreAsPercentage === "both"
              ? true
              : false,
        showAggregatedPercentage:
          marksAssessmentScale.showAggregationAspercentage === "per"
            ? true
            : marksAssessmentScale.showAggregationAspercentage ===
                "aggregateAndPer"
              ? true
              : false,
        showAggregatedScore:
          marksAssessmentScale.showAggregationAspercentage === "noPer"
            ? true
            : marksAssessmentScale.showAggregationAspercentage ===
                "aggregateAndPer"
              ? true
              : false,
        assessmentScaleMarkLevels: assessmentScaleMarkLevels,
        theoryMax:
          marksAssessmentScale.subjectType !== "practical"
            ? marksAssessmentScale.theoryFullMarks
            : 0,
        theoryMin:
          marksAssessmentScale.subjectType !== "practical"
            ? marksAssessmentScale.theoryPassMarks
            : 0,
        practicalMax:
          marksAssessmentScale.subjectType !== "theory"
            ? marksAssessmentScale.practicalFullMarks
            : 0,
        practicalMin:
          marksAssessmentScale.subjectType !== "theory"
            ? marksAssessmentScale.practicalPassMarks
            : 0,
      },
      assessmentModuleCode: arrLevel,
    };

    if (type === "create") {
      try {
        await createAssessmentScale(formData);
        setMarksAssessmentScale({
          scaleName: "",
          scoreName: "",
          totalMarks: "",
          subjectType: "theory",
          theoryPassMarks: "",
          theoryFullMarks: "",
          practicalPassMarks: "",
          practicalFullMarks: "",
          showScoreAsPercentage: "",
          calculateAggregate: false,
          showAggregationAspercentage: "",
        });
      } catch (error) {
        console.error("Create Failed", error);
      }
    } else {
      const updatedData = { ...formData, id: data.id };
      handleUpdateAssessment(updatedData);
    }
  };

  const handleAssociatePercentChange = (e, index) => {
    const { name, value } = e.target;
    const newAssociatePercent = associateScalePercent?.map((item, i) =>
      index === i ? { ...item, [name]: value } : item,
    );

    setAssociateScalePercent(newAssociatePercent);
  };

  const divideEqualParts = (min = 0, max = 100, parts) => {
    const step = (max - min) / parts;
    const ranges = [];

    for (let i = 0; i < parts; i++) {
      const minPercent = (min + i * step).toFixed(2);
      const maxPercent = (min + (i + 1) * step).toFixed(2);

      ranges.push({
        minPercent,
        maxPercent,
      });
    }

    return ranges;
  };

  useEffect(() => {
    if (assessmentScaleLevel?.assessmentScaleLevels?.length > 0) {
      const levels = assessmentScaleLevel?.assessmentScaleLevels;

      const ranges = divideEqualParts(
        marksAssessmentScale.rangeMin,
        marksAssessmentScale.rangeMax,
        levels.length,
      ).reverse();

      const data = levels.map((item, index) => ({
        id: item.id,
        grade: item?.level,
        descriptor: item?.descriptor,
        levelType: item?.levelType,
        rangeMin: ranges[index]?.minPercent,
        rangeMax: ranges[index]?.maxPercent,
      }));

      setAssociateScalePercent(data);
    }
  }, [assessmentScaleLevel, associateAssessmentLevel]);

  useEffect(() => {
    if (data && (type === "edit" || isView)) {
      const mark = data?.assessmentScaleMark;

      let showScoreAsPercentage = "";
      let showAggregationAspercentage = "";

      if (mark?.showScore && !mark?.showPercentage) {
        showScoreAsPercentage = "Nopctg";
      } else if (!mark?.showScore && mark?.showPercentage) {
        showScoreAsPercentage = "pctg";
      } else if (mark?.showScore && mark?.showPercentage) {
        showScoreAsPercentage = "both";
      }

      if (mark?.showAggregatedScore && !mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "noPer";
      } else if (!mark?.showAggregatedScore && mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "per";
      } else if (mark?.showAggregatedScore && mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "aggregateAndPer";
      }

      setAssociateScalePercent(mark?.assessmentScaleMarkLevels);

      const subjectType = () => {
        if (
          (mark?.theoryMin !== 0 || mark?.theoryMax !== 0) &&
          (mark?.practicalMin !== 0 || mark?.practicalMax !== 0)
        ) {
          return "both";
        }

        if (
          (mark?.theoryMin !== 0 || mark?.theoryMax !== 0) &&
          (mark?.practicalMin == 0 || mark?.practicalMax == 0)
        ) {
          return "theory";
        }

        if (
          (mark?.practicalMin !== 0 || mark?.practicalMax !== 0) &&
          (mark?.theoryMin === 0 || mark?.theoryMax === 0)
        ) {
          return "practical";
        }
      };

      setMarksAssessmentScale({
        scaleName: data?.scaleName,
        scoreName: data?.scoreName,
        totalMarks:
          Number(mark?.theoryMax ?? 0) + Number(mark?.practicalMax ?? 0),
        subjectType: subjectType(),
        theoryPassMarks: mark?.theoryMin,
        theoryFullMarks: mark?.theoryMax,
        practicalPassMarks: mark?.practicalMin,
        practicalFullMarks: mark?.practicalMax,
        showScoreAsPercentage,
        calculateAggregate: false,
        showAggregationAspercentage,
      });
    }
  }, [data, type, isView]);

  return (
    <>
      <div className={`arr-form grid ${type !== "create" ? "noShadow" : ""}`}>
        <div className="form-input">
          <label className="required" htmlFor="scaleName">
            Scale Name
          </label>
          <input
            className="form-control"
            type="text"
            value={marksAssessmentScale.scaleName}
            onChange={handleMarksAssessmentChange}
            name="scaleName"
            id="scaleName"
            placeholder="Enter scale name"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="scoreName">
            Score Name
          </label>
          <input
            className="form-control"
            value={marksAssessmentScale.scoreName}
            onChange={handleMarksAssessmentChange}
            type="text"
            name="scoreName"
            id="scoreName"
            placeholder="Enter score name"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="showScoreAsPercentage">
            Show Score As Percentage
          </label>
          <Select
            name={"showScoreAsPercentage"}
            value={
              showScoreAsPercentageOption?.find(
                (option) =>
                  option?.value === marksAssessmentScale.showScoreAsPercentage,
              ) || null
            }
            options={showScoreAsPercentageOption}
            onChange={(option) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                showScoreAsPercentage: option?.value,
              }))
            }
            isDisabled={isView}
          />
        </div>

        <Tooltip
          title={
            data?.isConfigured
              ? "You cannot change it because this assessment scale is currently being used in the assessment's Subject Configuration."
              : ""
          }
        >
          <div className="form-input">
            <label className="required" htmlFor="subjectType">
              Type
            </label>
            <Select
              name={"subjectType"}
              value={
                subjectTypeOptions?.find(
                  (option) =>
                    option?.value === marksAssessmentScale.subjectType,
                ) || null
              }
              options={subjectTypeOptions}
              onChange={handleSubjectTypeChange}
              isClearable={false}
              isDisabled={isView || data?.isConfigured}
            />
          </div>
        </Tooltip>

        <div className="form-input" style={{ display: "flex", gap: "8px" }}>
          <div style={{ flex: 1 }}>
            <label
              className={
                marksAssessmentScale.subjectType !== "practical" && "required"
              }
              htmlFor="theoryPassMarks"
            >
              Theory Pass Marks
            </label>
            <input
              type="number"
              name="theoryPassMarks"
              aria-label="theoryRangeMin"
              className="form-control"
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              value={marksAssessmentScale.theoryPassMarks}
              placeholder="Enter Pass Marks"
              readOnly={
                isView || marksAssessmentScale.subjectType === "practical"
              }
              disabled={
                isView || marksAssessmentScale.subjectType === "practical"
              }
            ></input>
          </div>
          <div style={{ flex: 1 }}>
            <label
              className={
                marksAssessmentScale.subjectType !== "practical" && "required"
              }
              htmlFor="theoryFullMarks"
            >
              Theory Full Marks
            </label>
            <input
              type="number"
              min={0}
              max={100}
              name="theoryFullMarks"
              aria-label="theoryRangeMax"
              className="form-control"
              value={marksAssessmentScale.theoryFullMarks}
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              placeholder="Max"
              readOnly={
                isView || marksAssessmentScale.subjectType === "practical"
              }
              disabled={
                isView || marksAssessmentScale.subjectType === "practical"
              }
            />
          </div>
        </div>

        <div className="form-input" style={{ display: "flex", gap: "8px" }}>
          <div style={{ flex: 1 }}>
            <label
              className={
                marksAssessmentScale.subjectType !== "theory" ? "required" : ""
              }
              htmlFor="practicalPassMarks"
            >
              Practical Pass Marks
            </label>
            <input
              type="number"
              name="practicalPassMarks"
              aria-label="practicalRangeMin"
              className="form-control"
              value={marksAssessmentScale.practicalPassMarks}
              placeholder="Enter Pass Marks"
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              readOnly={isView || marksAssessmentScale.subjectType === "theory"}
              disabled={isView || marksAssessmentScale.subjectType === "theory"}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              className={
                marksAssessmentScale.subjectType !== "theory" ? "required" : ""
              }
              htmlFor="practicalFullMarks"
            >
              Practical Full Marks
            </label>
            <input
              type="number"
              min={0}
              max={100}
              name="practicalFullMarks"
              aria-label="practicalRangeMax"
              className="form-control"
              value={marksAssessmentScale.practicalFullMarks}
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              placeholder="Max"
              readOnly={isView || marksAssessmentScale.subjectType === "theory"}
              disabled={isView || marksAssessmentScale.subjectType === "theory"}
            />
          </div>
        </div>

        <div className="form-input">
          <label className="required" htmlFor="totalMarks">
            Total Marks
          </label>
          <input
            aria-label="rangeMax"
            type="number"
            min={0}
            max={100}
            name="totalMarks"
            className="form-control"
            placeholder="Total Marks"
            value={marksAssessmentScale.totalMarks}
            disabled={true}
            readOnly={true}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="showAggregationAspercentage">
            Show Aggregation As Percentage
          </label>
          <Select
            name={"showAggregationAspercentage"}
            options={showAggregationAsPercentageOption}
            value={
              showAggregationAsPercentageOption?.find(
                (o) =>
                  o.value === marksAssessmentScale.showAggregationAspercentage,
              ) || null
            }
            onChange={(option) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                showAggregationAspercentage: option?.value,
              }))
            }
            isDisabled={isView}
          />
        </div>

        <div
          className="form-input"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <label htmlFor="calculateAggregate">
            Calculate Aggregate (of Subjects)
          </label>
          <Switch
            className="arr-switch"
            checked={marksAssessmentScale.calculateAggregate}
            onClick={(e) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                calculateAggregate: !prev.calculateAggregate,
              }))
            }
            inputProps={{ "aria-label": "controlled" }}
            disabled={isView}
          />
        </div>
      </div>

      {!isView && (
        <div className="action-btns end" style={{ marginTop: "24px" }}>
          {type === "create" && (
            <Button
              variant="outline"
              onClick={() => {
                setAssociateLevelModal((prev) => !prev);
                const data = {
                  assessmentModuleCode: arrLevel,
                  assessmentScaleTypeCode: "sct-lvl",
                };
                getAssociateAssessmentLevel(data);
              }}
            >
              Associate Levels
            </Button>
          )}

          {type !== "create" && (
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
          )}

          <Button disabled={loading.create || updating} onClick={handleSubmit}>
            {loading.create || updating ? (
              <Spinner size={"sm"} color="white" />
            ) : type === "create" ? (
              "Create Assessment Scale"
            ) : (
              "Update Assessment Scale"
            )}
          </Button>
        </div>
      )}

      <CustomModal
        isOpen={associateLevelModal}
        toggle={() => setAssociateLevelModal((prev) => !prev)}
        modalHeader={"Associate Levels"}
        size="md"
        children={
          <div>
            <div style={{ position: "relative" }}>
              <Select
                options={associateAssessmentLevelOptions}
                value={associateSelectInput}
                onChange={(option) => {
                  setAssociateSeletInput(option);
                  selectAssessmentScaleLevel(option?.value);
                }}
              />
            </div>
            <div className="arr-table-container">
              <table className="arr-table">
                <thead>
                  <tr>
                    <th>in (%)</th>
                    <th>Grade</th>
                    <th>Descriptor</th>
                  </tr>
                </thead>
                <tbody>
                  {associateScalePercent?.map((item, index) => {
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="flex" style={{ gap: "10px" }}>
                            <input
                              type="number"
                              onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") {
                                  e.preventDefault();
                                }
                              }}
                              min={0}
                              className="form-control"
                              name={"minPercent"}
                              value={associateScalePercent[index]?.rangeMin}
                              onChange={(e) =>
                                handleAssociatePercentChange(e, index)
                              }
                            />
                            <span>-</span>
                            <input
                              type="number"
                              onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") {
                                  e.preventDefault();
                                }
                              }}
                              min={0}
                              className="form-control"
                              name={"maxPercent"}
                              value={associateScalePercent[index]?.rangeMax}
                              onChange={(e) =>
                                handleAssociatePercentChange(e, index)
                              }
                            />
                          </div>
                        </td>
                        <td>
                          {item?.levelType === "image" ? (
                            <img
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                              src={
                                URL.imageSelectURL +
                                associateScalePercent[index]?.grade
                              }
                            />
                          ) : (
                            (associateScalePercent[index]?.grade ?? "N/A")
                          )}
                        </td>
                        <td>
                          {associateScalePercent[index]?.descriptor ?? "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="action-btns end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAssociateLevelModal((prev) => !prev);
                  setAssociateScalePercent([]);
                  setAssociateSeletInput(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => setAssociateLevelModal((prev) => !prev)}
              >
                Done
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default MarksAssessmentForm;
